import { useMsal } from "@azure/msal-react";
import { getToken, useLogin } from "../../authConfig";
import { Spinner } from "@fluentui/react";
import { Button } from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { HistoryData, HistoryItem } from "../HistoryItem";
import { Answers, HistoryProviderOptions } from "../HistoryProviders/IProvider";
import { useHistoryManager, HistoryMetaData } from "../HistoryProviders";
import { useTranslation } from "react-i18next";
import styles from "./HistoryPanel.module.css";

const HISTORY_COUNT_PER_LOAD = 20;

export const HistoryPanel = ({
    provider,
    isOpen,
    notify,
    onToggle,
    onChatSelected,
    activeSessionId,
    uploadDisabled,
    maxUploadSizeMb
}: {
    provider: HistoryProviderOptions;
    isOpen: boolean;
    notify: boolean;
    onToggle: () => void;
    onChatSelected: (answers: Answers, id: string) => void;
    activeSessionId?: string | null;
    uploadDisabled?: boolean;
    maxUploadSizeMb?: number;
}) => {
    const historyManager = useHistoryManager(provider);
    const [history, setHistory] = useState<HistoryMetaData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMoreHistory, setHasMoreHistory] = useState(false);

    const client = useLogin ? useMsal().instance : undefined;

    // Reset whenever the panel opens with notify=true. Also reset when the active
    // session id changes — that's a reliable signal that a turn just landed (or
    // the user loaded a different chat from history) and the in-memory list is
    // potentially stale on timestamp/order/message_count.
    useEffect(() => {
        if (!isOpen) return;
        if (notify) {
            setHistory([]);
            historyManager.resetContinuationToken();
            setHasMoreHistory(true);
        }
    }, [isOpen, notify, activeSessionId]);

    const loadingRef = useRef(false);
    const loadMoreHistory = async () => {
        // The IntersectionObserver in InfiniteLoadingButton can fire func() repeatedly; a
        // ref guard (not the async isLoading state) prevents overlapping fetches that would
        // pull and append the same continuation page twice.
        if (loadingRef.current) return;
        loadingRef.current = true;
        setIsLoading(true);
        try {
            const token = client ? await getToken(client) : undefined;
            const items = await historyManager.getNextItems(HISTORY_COUNT_PER_LOAD, token);
            if (items.length === 0) {
                setHasMoreHistory(false);
            }
            // Dedupe by id when accumulating. Sessions are ordered by timestamp DESC and each
            // turn bumps the active session's timestamp, so a session can shift across the
            // pagination boundary and re-appear on a later page. Appending blindly would then
            // produce duplicate React keys (and the "duplicated and/or omitted children" warning).
            setHistory(prevHistory => {
                const seen = new Set(prevHistory.map(i => i.id));
                const merged = prevHistory.slice();
                for (const item of items) {
                    if (!seen.has(item.id)) {
                        seen.add(item.id);
                        merged.push(item);
                    }
                }
                return merged;
            });
        } finally {
            loadingRef.current = false;
            setIsLoading(false);
        }
    };

    const handleSelect = async (id: string) => {
        const token = client ? await getToken(client) : undefined;
        const item = await historyManager.getItem(id, token);
        if (item) {
            onChatSelected(item, id);
        }
    };

    const handleDelete = async (id: string) => {
        const token = client ? await getToken(client) : undefined;
        await historyManager.deleteItem(id, token);
        setHistory(prevHistory => prevHistory.filter(item => item.id !== id));
    };

    // Defensive client-side sort: backend returns timestamp DESC but in-memory items
    // can drift if the active session bumps server-side after the panel was loaded.
    // Sorting on every render keeps the active conversation pinned at the top.
    const groupedHistory = useMemo(() => {
        const sorted = [...history].sort((a, b) => b.timestamp - a.timestamp);
        return groupHistory(sorted);
    }, [history]);

    const { t } = useTranslation();

    return (
        <>
            {/* Slide-out panel */}
            <div className={`${styles.container} ${isOpen ? styles.containerOpen : ""}`}>
                <div className={styles.header}>
                    <span className={styles.headerTitle}>{t("history.chatHistory")}</span>
                    <Button appearance="subtle" icon={<Dismiss24Regular />} onClick={onToggle} aria-label="Close" />
                </div>
                <div className={styles.listContainer}>
                    {Object.entries(groupedHistory).map(([group, items]) => (
                        <div key={group} className={styles.group}>
                            <p className={styles.groupLabel}>{group.startsWith("history.") ? t(group) : group}</p>
                            {items.map(item => (
                                <HistoryItem
                                    key={item.id}
                                    item={item}
                                    onSelect={handleSelect}
                                    onDelete={handleDelete}
                                    isActive={!!activeSessionId && item.id === activeSessionId}
                                />
                            ))}
                        </div>
                    ))}
                    {isLoading && <Spinner style={{ marginTop: "10px" }} />}
                    {history.length === 0 && !isLoading && <p className={styles.noHistory}>{t("history.noHistory")}</p>}
                    {hasMoreHistory && !isLoading && <InfiniteLoadingButton func={loadMoreHistory} />}
                </div>
            </div>
        </>
    );
};

function groupHistory(history: HistoryData[]) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastMonth = new Date(today);
    lastMonth.setDate(lastMonth.getDate() - 30);

    return history.reduce(
        (groups, item) => {
            const itemDate = new Date(item.timestamp);
            let group;

            if (itemDate >= today) {
                group = "history.today";
            } else if (itemDate >= yesterday) {
                group = "history.yesterday";
            } else if (itemDate >= lastWeek) {
                group = "history.last7days";
            } else if (itemDate >= lastMonth) {
                group = "history.last30days";
            } else {
                group = itemDate.toLocaleDateString(undefined, { year: "numeric", month: "long" });
            }

            if (!groups[group]) {
                groups[group] = [];
            }
            groups[group].push(item);
            return groups;
        },
        {} as Record<string, HistoryData[]>
    );
}

const InfiniteLoadingButton = ({ func }: { func: () => void }) => {
    const buttonRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        if (buttonRef.current) {
                            func();
                        }
                    }
                });
            },
            {
                root: null,
                threshold: 0
            }
        );

        if (buttonRef.current) {
            observer.observe(buttonRef.current);
        }

        return () => {
            if (buttonRef.current) {
                observer.unobserve(buttonRef.current);
            }
        };
    }, []);

    return <button ref={buttonRef} onClick={func} />;
};
